import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import PageTitle from '../../../components/PageTitle/PageTitle';
import AlertModal from '../../../components/AlertModal/AlertModal';
import Button from '../../../components/Button/Button';
import { DynamicInput, DynamicSelect, DynamicCheckbox } from '../../../components/Inputs/Inputs';
import './CsatConfig.scss';

function extractErrorMessage(err, fallback = 'Ocorreu um erro') {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    return detail.message || detail.error_code || fallback;
  }
  return detail || err?.message || fallback;
}

export default function CsatConfig() {
  const { axiosAuth, currentUser, accessibleHotels = [] } = useContext(AuthContext);
  const isSuper = currentUser?.role === 'superadmin';

  const [allHotels, setAllHotels] = useState([]);
  const [groupNameById, setGroupNameById] = useState({});
  const [hotelId, setHotelId] = useState('');

  const [license, setLicense] = useState(null);
  const [licenseSaving, setLicenseSaving] = useState(false);
  const [notLicensed, setNotLicensed] = useState(false);

  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [handoffAvailable, setHandoffAvailable] = useState(false);

  const [errorModal, setErrorModal] = useState({ open: false, title: '', message: '' });
  const openError = useCallback((title, err) => {
    setErrorModal({ open: true, title, message: extractErrorMessage(err) });
  }, []);
  const closeError = useCallback(
    () => setErrorModal({ open: false, title: '', message: '' }),
    [],
  );

  const [infoModal, setInfoModal] = useState({ open: false, title: '', message: '' });

  useEffect(() => {
    if (!isSuper) return;
    (async () => {
      try {
        const [{ data: hotelsData }, { data: groupsData }] = await Promise.all([
          axiosAuth.get('/hotels'),
          axiosAuth.get('/groups'),
        ]);
        const nameByGroup = {};
        (groupsData || []).forEach((g) => {
          nameByGroup[g.id] = g.name;
        });
        setGroupNameById(nameByGroup);
        setAllHotels(hotelsData || []);
      } catch (err) {
        openError('Erro ao carregar hotéis', err);
      }
    })();
  }, [isSuper, axiosAuth, openError]);

  const hotelOptions = useMemo(() => {
    if (isSuper) {
      return allHotels.map((h) => ({
        value: String(h.id),
        label: groupNameById[h.group_id] ? `${h.name} — ${groupNameById[h.group_id]}` : h.name,
      }));
    }
    return accessibleHotels.map((h) => ({ value: String(h.id), label: h.name }));
  }, [isSuper, allHotels, groupNameById, accessibleHotels]);

  useEffect(() => {
    if (!hotelId && hotelOptions.length === 1) {
      setHotelId(hotelOptions[0].value);
    }
  }, [hotelOptions, hotelId]);

  const fetchLicense = useCallback(async () => {
    if (!isSuper || !hotelId) return;
    try {
      const { data } = await axiosAuth.get('/modules/status', {
        params: { hotel_id: Number(hotelId), module_code: 'CSAT' },
      });
      setLicense(data?.modules?.[0] || null);
    } catch (err) {
      openError('Erro ao consultar licenciamento', err);
    }
  }, [isSuper, hotelId, axiosAuth, openError]);

  const fetchConfig = useCallback(async () => {
    if (!hotelId) return;
    try {
      const [{ data: settingsData }, { data: handoffStatus }] = await Promise.all([
        axiosAuth.get('/csat/config/settings', { params: { hotel_id: Number(hotelId) } }),
        axiosAuth
          .get('/modules/status', {
            params: { hotel_id: Number(hotelId), module_code: 'HUMAN_HANDOFF' },
          })
          .catch(() => ({ data: null })),
      ]);
      setSettingsForm({
        enabled: settingsData.enabled,
        ask_comment_negative: settingsData.ask_comment_negative,
        offer_operator_negative: settingsData.offer_operator_negative,
        question_pt: settingsData.question_pt,
        question_en: settingsData.question_en,
        question_es: settingsData.question_es,
        thanks_pt: settingsData.thanks_pt,
        thanks_en: settingsData.thanks_en,
        thanks_es: settingsData.thanks_es,
      });
      setHandoffAvailable(Boolean(handoffStatus?.data?.modules?.[0]?.licensed));
      setNotLicensed(false);
    } catch (err) {
      if (err?.response?.data?.detail?.error_code === 'MODULE_NOT_LICENSED') {
        setNotLicensed(true);
        setSettingsForm(null);
      } else {
        openError('Erro ao carregar configuração da Satisfação', err);
      }
    }
  }, [hotelId, axiosAuth, openError]);

  useEffect(() => {
    setSettingsForm(null);
    setNotLicensed(false);
    setLicense(null);
    if (!hotelId) return;
    fetchLicense();
    fetchConfig();
  }, [hotelId, fetchLicense, fetchConfig]);

  async function toggleLicense(enabled) {
    setLicenseSaving(true);
    try {
      await axiosAuth.post('/modules/licenses', {
        module_code: 'CSAT',
        hotel_id: Number(hotelId),
        enabled,
        reason: 'manual',
      });
      await fetchLicense();
      await fetchConfig();
      setInfoModal({
        open: true,
        title: enabled ? 'Módulo licenciado' : 'Módulo desativado',
        message: enabled
          ? 'O Inquérito de Satisfação foi licenciado para este hotel.'
          : 'O Inquérito de Satisfação foi desativado para este hotel.',
      });
    } catch (err) {
      openError('Erro ao alterar licenciamento', err);
    } finally {
      setLicenseSaving(false);
    }
  }

  async function saveSettings() {
    if (!settingsForm) return;
    setSavingSettings(true);
    try {
      await axiosAuth.put('/csat/config/settings', settingsForm, {
        params: { hotel_id: Number(hotelId) },
      });
      setInfoModal({ open: true, title: 'Guardado', message: 'Definições atualizadas.' });
    } catch (err) {
      openError('Erro ao guardar definições', err);
    } finally {
      setSavingSettings(false);
    }
  }

  return (
    <div className="csat-config-page">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || 'Ocorreu um erro'}
          message={errorModal.message}
          onClose={closeError}
        />
      )}
      {infoModal.open && (
        <AlertModal
          variant="info"
          title={infoModal.title}
          message={infoModal.message}
          onClose={() => setInfoModal({ open: false, title: '', message: '' })}
        />
      )}

      <PageTitle link="" title="Configurar Satisfação" />

      {hotelOptions.length > 1 && (
        <div className="hotel-selector">
          <DynamicSelect
            label="Hotel"
            placeholder="Escolher um hotel"
            value={hotelId}
            onChange={(e) => setHotelId(e.target.value)}
            options={hotelOptions}
          />
        </div>
      )}

      {!hotelId ? (
        <div className="empty-page">
          <p>Escolhe um hotel para configurar a Satisfação.</p>
        </div>
      ) : (
        <>
          {isSuper && (
            <div className="license-panel">
              <div className="license-status">
                <span className={`badge ${license?.licensed ? 'on' : 'off'}`}>
                  {license?.licensed ? 'Módulo licenciado' : 'Módulo não licenciado'}
                </span>
              </div>
              <Button
                type="button"
                variant={license?.licensed ? 'secondary' : 'primary'}
                disabled={licenseSaving}
                onClick={() => toggleLicense(!license?.licensed)}
              >
                {license?.licensed ? 'Desativar módulo' : 'Licenciar módulo'}
              </Button>
            </div>
          )}

          {notLicensed ? (
            <div className="empty-page">
              <p>
                {isSuper
                  ? 'Licencia o módulo Inquérito de Satisfação acima para configurar este hotel.'
                  : 'Este hotel não tem o módulo Inquérito de Satisfação licenciado. Fala com um SuperAdmin para o ativar.'}
              </p>
            </div>
          ) : (
            settingsForm && (
              <div className="settings-panel">
                <div className="row checkbox-row">
                  <DynamicCheckbox
                    label="Ativar inquérito no widget"
                    name="enabled"
                    checked={settingsForm.enabled}
                    onChange={(name, value) =>
                      setSettingsForm((p) => ({ ...p, enabled: value }))
                    }
                    disabled={savingSettings}
                  />
                </div>
                <div className="row checkbox-row">
                  <DynamicCheckbox
                    label="Pedir comentário nas avaliações 1–2"
                    name="ask_comment_negative"
                    checked={settingsForm.ask_comment_negative}
                    onChange={(name, value) =>
                      setSettingsForm((p) => ({ ...p, ask_comment_negative: value }))
                    }
                    disabled={savingSettings}
                  />
                </div>
                <div className="row checkbox-row">
                  <DynamicCheckbox
                    label="Oferecer operador em avaliações negativas"
                    name="offer_operator_negative"
                    checked={settingsForm.offer_operator_negative}
                    onChange={(name, value) =>
                      setSettingsForm((p) => ({ ...p, offer_operator_negative: value }))
                    }
                    disabled={savingSettings || !handoffAvailable}
                  />
                  {!handoffAvailable && (
                    <span className="help">
                      Só disponível com o módulo Atendimento Humano licenciado para este hotel.
                    </span>
                  )}
                </div>

                <h3>Pergunta</h3>
                <div className="row three-col">
                  <DynamicInput
                    type="text"
                    label="PT"
                    name="question_pt"
                    value={settingsForm.question_pt}
                    onChange={(name, value) =>
                      setSettingsForm((p) => ({ ...p, question_pt: value }))
                    }
                    disabled={savingSettings}
                  />
                  <DynamicInput
                    type="text"
                    label="EN"
                    name="question_en"
                    value={settingsForm.question_en}
                    onChange={(name, value) =>
                      setSettingsForm((p) => ({ ...p, question_en: value }))
                    }
                    disabled={savingSettings}
                  />
                  <DynamicInput
                    type="text"
                    label="ES"
                    name="question_es"
                    value={settingsForm.question_es}
                    onChange={(name, value) =>
                      setSettingsForm((p) => ({ ...p, question_es: value }))
                    }
                    disabled={savingSettings}
                  />
                </div>

                <h3>Agradecimento</h3>
                <div className="row three-col">
                  <DynamicInput
                    type="text"
                    label="PT"
                    name="thanks_pt"
                    value={settingsForm.thanks_pt}
                    onChange={(name, value) =>
                      setSettingsForm((p) => ({ ...p, thanks_pt: value }))
                    }
                    disabled={savingSettings}
                  />
                  <DynamicInput
                    type="text"
                    label="EN"
                    name="thanks_en"
                    value={settingsForm.thanks_en}
                    onChange={(name, value) =>
                      setSettingsForm((p) => ({ ...p, thanks_en: value }))
                    }
                    disabled={savingSettings}
                  />
                  <DynamicInput
                    type="text"
                    label="ES"
                    name="thanks_es"
                    value={settingsForm.thanks_es}
                    onChange={(name, value) =>
                      setSettingsForm((p) => ({ ...p, thanks_es: value }))
                    }
                    disabled={savingSettings}
                  />
                </div>

                <div className="settings-actions">
                  <Button type="button" onClick={saveSettings} disabled={savingSettings}>
                    Guardar definições
                  </Button>
                </div>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
